import { HttpClient } from '@angular/common/http';
import { Injectable, DOCUMENT, inject } from '@angular/core';
import { environment } from '@mogenius/environments/environment';
import {
  GitConnectionTypeEnum,
  GitHubActionDTO,
  GithubRepositoryFileTreeDto,
  GithubRepositoryFileTreeNestedDto,
  GithubUserDto,
  GithubWorkflowDto,
  GitRepositoryDto
} from '@mogenius/client-sdk/core-dto';
import { NotificationService } from '@mogenius/shared/services/notification.service';
import { plainToInstance } from 'class-transformer';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  DEFAULT_GIT_SERVICE_SEARCH_REQUEST,
  DEFAULT_GIT_SERVICE_SEARCH_RESPONSE,
  GitBaseService
} from '../git-base.service';
import { MoUtils } from '@mogenius/client-sdk/utils';

@Injectable({
  providedIn: 'root'
})
export class GithubService extends GitBaseService {
  protected readonly _http: HttpClient;
  protected readonly _notificationService: NotificationService;
  protected readonly _document: Document;

  protected _apiBase = environment.githubService;

  constructor() {
    const _http = inject(HttpClient);
    const _notificationService = inject(NotificationService);
    const _document = inject<Document>(DOCUMENT);

    super();

    this._http = _http;
    this._notificationService = _notificationService;
    this._document = _document;
  }

  public appPublicLink(
    callbackUrl?: string,
    organizationId?: string,
    projectId?: string,
    hideLoadingSpinner: boolean = false
  ): Observable<string> {
    const headers: { [key: string]: string } = {
      'Content-Type': environment.githubService.appPublicLink.header.contentType,
      hideLoadingSpinner: '1', // hideLoadingSpinner ? '1' : '0',
      'x-callback-url': callbackUrl ?? `${window.location.href}`
    };
    if (projectId) {
      headers['project-id'] = projectId;
    }
    if (organizationId) {
      headers['organization-id'] = organizationId;
    }
    const endpoint = this.getEndpoint(this._apiBase.appPublicLink);
    return this._http.request(endpoint.method, endpoint.url, {
      headers: headers,
      responseType: 'text'
    });
  }

  public selectInstallationAsConnection(
    installationId: number,
    installationsKey: string,
    organizationId?: string,
    projectId?: string,
    hideLoadingSpinner: boolean = false
  ): Observable<boolean> {
    const headers: { [key: string]: string } = {
      'Content-Type': environment.githubService.selectInstallationAsConnection.header.contentType,
      hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0',
    };
    if (projectId) {
      headers['project-id'] = projectId;
    }
    if (organizationId) {
      headers['organization-id'] = organizationId;
    }
    const endpoint = this.getEndpoint(this._apiBase.selectInstallationAsConnection);
    return this._http.request<boolean>(endpoint.method, endpoint.url, {
      headers: headers,
      body: {
        installationId: `${installationId}`,
        installationsKey: `${installationsKey}`
      }
    });
  }

  public actionsWorkflows(fullName: string, hideLoadingSpinner: boolean = false): Observable<GithubWorkflowDto[]> {
    const endpoint = this.getEndpoint(this._apiBase.actionsWorkflows);
    return this._http
      .request<GithubWorkflowDto[]>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header.contentType,
          hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0'
        },
        params: {
          fullName: fullName
        }
      })
      .pipe(
        map((response: GithubWorkflowDto[]) =>
          response.map((item: GithubWorkflowDto) =>
            plainToInstance(GithubWorkflowDto, item, { excludeExtraneousValues: true })
          )
        )
      );
  }

  public actionsWorkflow(
    fullName: string,
    path: string,
    hideLoadingSpinner: boolean = false
  ): Observable<GitHubActionDTO> {
    const endpoint = this.getEndpoint(this._apiBase.actionsWorkflow);
    return this._http
      .request<GitHubActionDTO>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header.contentType,
          hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0'
        },
        params: {
          fullName: fullName,
          path: path
        }
      })
      .pipe(
        map((response: GitHubActionDTO) =>
          plainToInstance(GitHubActionDTO, response, { excludeExtraneousValues: true })
        )
      );
  }

  public getUserInfo(gitConnection?: {
    type: GitConnectionTypeEnum;
    username: string;
    password: string;
  }): Observable<GithubUserDto> {
    const endpoint = this.getEndpoint(this._apiBase.userInfo);

    const headers: { [key: string]: string } = {
      'Content-Type': `${endpoint.header.contentType}`,
      hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0'
    };
    if (gitConnection) {
      headers['git-credentials'] = btoa(JSON.stringify(gitConnection));
    }

    return this._http
      .request<GithubUserDto>(endpoint.method, endpoint.url, {
        headers: headers
      })
      .pipe(
        map((res: GithubUserDto) => {
          return MoUtils.transformToDto(GithubUserDto, res);
        })
      );
  }

  public repositoryBranchTree(
    cloneUrl: string,
    branch: string,
    raw: boolean,
    type: 'tree' | 'blob' | 'all',
    hideLoadingSpinner: boolean = false,
    gitConnection?: {
      type: GitConnectionTypeEnum;
      username: string;
      password: string;
    }
  ): Observable<GithubRepositoryFileTreeDto[] | GithubRepositoryFileTreeNestedDto[]> {
    const endpoint = this.getEndpoint(this._apiBase.repositoryBranchTree);

    const headers: { [key: string]: string } = {
      'Content-Type': `${endpoint.header.contentType}`,
      hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0'
    };
    if (gitConnection) {
      headers['git-credentials'] = btoa(JSON.stringify(gitConnection));
    }
    return this._http
      .request<any>(endpoint.method, endpoint.url, {
        headers: headers,
        params: {
          cloneUrl: cloneUrl,
          branch: branch,
          raw: raw,
          type: type
        }
      })
      .pipe(
        map((res) => {
          if (raw) {
            return MoUtils.transformToDtoList(GithubRepositoryFileTreeDto, res);
          }
          return MoUtils.transformToDtoList(GithubRepositoryFileTreeNestedDto, res);
        })
      );
  }

  public deleteRepository(repositoryList: GitRepositoryDto[], hideLoadingSpinner: boolean = false) {
    const endpoint = this.getEndpoint(this._apiBase.deleteRepository);
    return this._http.request<boolean>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header.contentType,
        hideLoadingSpinner: hideLoadingSpinner ? '1' : '0'
      },
      body: repositoryList.map((item) => item.clone_url)
    });
  }

  public isBranchProtected(
    cloneUrl: string,
    branch: string,
    gitConnection?: {
      type: GitConnectionTypeEnum;
      username: string;
      password: string;
    }
  ): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.isBranchProtected);
    const headers: { [key: string]: string } = {
      'Content-Type': `${endpoint.header.contentType}`,
      hideLoadingSpinner: '1',
      hideErrorMessage: '1'
    };
    if (gitConnection) {
      headers['git-credentials'] = btoa(JSON.stringify(gitConnection));
    }
    return this._http
      .request<boolean>(endpoint.method, endpoint.url, {
        headers: headers,
        params: {
          cloneUrl,
          branch
        }
      })
      .pipe(catchError(() => of(false)));
  }

  public cleanUp(): void {
    this._searchRequest$.next(DEFAULT_GIT_SERVICE_SEARCH_REQUEST);
    this._searchResponse$.next(DEFAULT_GIT_SERVICE_SEARCH_RESPONSE);
  }
}
